import { Stack } from "@mui/material";
import { TableHeader } from "./TableHeader";
import {
  CampaignMember,
  MembersUpdateValues,
} from "../../../types/companyTypes";
import { useGetCampaignMembersQuery } from "../../../api";
import { useStateWithSearchParams } from "../../../hooks";
import { Order } from "../../../types";
import { RowItem } from "./RowItem";
import { memo, useEffect } from "react";
import { ToTopBtn } from "../../ui/buttons";
import { EmptyRow } from "./EmptyRow";
import { CustomLoader } from "../../ui/CustomLoader";
import { mockCompanyMembers } from "../../../types/genereal";
import { QUERY_SEPARATOR } from "../../../constant";
import { InfiniteScroll } from "../../ui";
import { NoContactsBlock } from "./NoContactsBlock";

type SearchParams = {
  sort?: string;
  min?: string;
  max?: string;
  roles?: string;
  data_source?: string;
};

export const CompanyTable = memo(
  ({
    isEditMode,
    account_id,
    page,
    setPage,
    updatedValues,
    updateHandler,
    setCommitteeSelected,
    setClusterSelected,
  }: {
    isEditMode: boolean;
    account_id: string;
    page: number;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    setCommitteeSelected: React.Dispatch<React.SetStateAction<number>>;
    setClusterSelected: React.Dispatch<React.SetStateAction<number>>;
    updatedValues: {
      [key: string]: MembersUpdateValues;
    };
    updateHandler: (values: MembersUpdateValues) => void;
  }) => {
    const { searchDict } = useStateWithSearchParams<SearchParams>({
      sort: undefined,
      min: undefined,
      max: undefined,
      roles: undefined,
      data_source: undefined,
    });

    const { data, isLoading, isFetching } = useGetCampaignMembersQuery(
      {
        account_id,
        page,
        per_page: 40,
        sort: searchDict?.sort?.split("-")[0] || "committee_probability",
        order: (searchDict?.sort?.split("-")[1] as Order) || Order.DESC,
        roles: searchDict.roles?.split(QUERY_SEPARATOR),
        data_source: searchDict.data_source?.split(QUERY_SEPARATOR),
        is_enriched: undefined,
        min_percent: searchDict.min ? Number(searchDict.min) : undefined,
        max_percent: searchDict.max ? Number(searchDict.max) : undefined,
      },
      { refetchOnMountOrArgChange: true },
    );

    const canLoadMore = page + 1 < (data?.total_pages || 0);

    useEffect(() => {
      setPage(0);
    }, [
      searchDict.sort,
      searchDict.roles,
      searchDict.max,
      searchDict.min,
      searchDict.data_source,
    ]);

    const isFilters =
      searchDict?.sort ||
      searchDict?.min ||
      searchDict?.max ||
      searchDict?.data_source ||
      searchDict?.roles;

    if (isLoading || !data) return <CustomLoader />;

    if (!isFilters && data.items.length === 0) {
      return (
        <Stack paddingY="24px">
          <TableHeader />
          {isFetching ? (
            <>
              {[1, 2, 3].map((el) => (
                <EmptyRow key={el} />
              ))}
            </>
          ) : (
            <>
              {mockCompanyMembers.map((data) => (
                <RowItem
                  key={data.id}
                  setCommitteeSelected={setCommitteeSelected}
                  setClusterSelected={setClusterSelected}
                  updatedValues={updatedValues[data.id]}
                  isEditMode={isEditMode}
                  updateHandler={updateHandler}
                  {...data}
                />
              ))}
            </>
          )}
        </Stack>
      );
    }

    return (
      <InfiniteScroll<CampaignMember>
        hasMore={canLoadMore}
        isLoading={isFetching}
        loadMore={() => setPage((prev) => prev + 1)}
        page={page}
        items={data?.items || []}
        startPage={0}
      >
        {({ showScrollTop, items }) => (
          <Stack paddingY="24px">
            <TableHeader />
            {items.length > 0 ? (
              items.map((data) => (
                <RowItem
                  key={data.id}
                  {...data}
                  isEditMode={isEditMode}
                  updatedValues={updatedValues[data.id]}
                  updateHandler={updateHandler}
                  setCommitteeSelected={setCommitteeSelected}
                  setClusterSelected={setClusterSelected}
                />
              ))
            ) : (
              <NoContactsBlock />
            )}

            {canLoadMore && [1, 2, 3].map((el) => <EmptyRow key={el} />)}

            {showScrollTop && <ToTopBtn />}
          </Stack>
        )}
      </InfiniteScroll>
    );
  },
);
